import React, { ReactNode } from "react";
import ProfileContainer from "./ProfileContainer";
import { Button, CircularProgress, Grid, Link, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useInvestorRiskRatingStore from "../zstore/investorRiskRating";
import { useShallow } from "zustand/react/shallow";
import useInvestorMarkerStore from "../zstore/investorMarkers";
import { find, maxBy } from "lodash";
import { MarkerGroups, NoneGroupMarkers } from "../constant/markerConstant";
import MarkerDisplay from "./MarkerDisplay";
import MarkerUpdateButton from "./MarkerUpdateButton";
import { formatTimestamp, formatDateAndTime } from "../utils/date";
import { RiskRatingAPI } from "../api";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import MarkerDialog, { useMarkerDialog } from "./MarkerDialog";
import useHighlightStore, { ItemType } from "../zstore/highlightStore";
import { useSelector } from "react-redux";
import { selectors } from "../amlSlice";

type RiskRatingProfileProps = {
  personId: string;
};

const RISK_RATING_DATA_PLACEHOLDER = "No Data";

const RiskRatingProfile: React.FC<RiskRatingProfileProps> = ({ personId }) => {
  const { latestRiskRatingResult, loading: riskRatingLoading } =
    useInvestorRiskRatingStore(
      useShallow((state) => ({
        latestRiskRatingResult: state.getLatestRiskRatingResult(),
        loading: state.loading,
      }))
    );
  const { amlDetail, amlDetailLoading } = useInvestorAmlDetailStore(
    useShallow((state) => ({
      amlDetail: state.data,
      amlDetailLoading: state.loading,
    }))
  );
  const removeHighlightedItem = useHighlightStore((state) => state.removeItem);
  const markers = useSelector(selectors.markers);
  const {
    open: markerDialogOpen,
    closeDialog: closeMarkerDialog,
    formConfig: markerDialogFormConfig,
    initialValue: markerDialogInitial,
    openMarkerAddDialog,
    openMarkerUpdateDialog,
    openGroupMarkerAddDialog,
  } = useMarkerDialog();

  const {
    data: investorMarkers,
    loading: investorMarkerLoading,
    fetchData: refetchInvestorMarkers,
  } = useInvestorMarkerStore();

  const queryClient = useQueryClient();
  const { data: riskEngineLink } = useQuery({
    queryKey: ["riskEngineLink", personId],
    queryFn: () => RiskRatingAPI.getInvestorRiskEngineLink(personId),
    enabled: !!personId,
  });

  const {
    data: riskEngineRequests,
    isLoading: requestsLoading,
  } = useQuery({
    queryKey: ["riskEngineRequests", personId],
    queryFn: () => RiskRatingAPI.getInvestorRiskEngineRequests(personId),
    enabled: !!personId,
  });

  const requestMutation = useMutation({
    mutationFn: () => RiskRatingAPI.requestManualRiskEngineAssessments(personId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["riskEngineRequests", personId] });
    },
  });

  const toNumeric = (d: number, t: number) => Number(String(d) + `0${String(t)}`.slice(-6));
  const latestRequest = React.useMemo(() => {
    const items = (riskEngineRequests || [])
      .map((r: any) => ({ date: r.processedDate, time: r.processedTime, row: r }))
      .filter((v: any) => typeof v.date === "number" && typeof v.time === "number");
    if (!items.length) return null;
    return maxBy(items, (x: any) => toNumeric(x.date, x.time));
  }, [riskEngineRequests]);

  const lastRequestedAt = latestRequest ? formatDateAndTime(latestRequest.date, latestRequest.time) : null;
  const manualRiskCalcStatus: number | null = latestRequest
    ? latestRequest.row.manualRiskCalculationStatus ?? latestRequest.row.status ?? latestRequest.row.statusCode ?? null
    : null;
  const kycMarker = find(investorMarkers || [], {
    code: NoneGroupMarkers.KYC,
    removed: false,
  });
  const riskRatingMarker = find(investorMarkers || [], {
    groupCode: MarkerGroups.RSKS,
    removed: false,
  });

  return (
    <ProfileContainer id="risk-rating-section" title="Risk Rating">
      <Grid container>
        <Grid item md={6} sm={12}>
          <Stack direction="column" rowGap="10px">
            <Item title="Calculated Risk Level: ">
              {riskRatingLoading ? (
                <CircularProgress size="1rem" />
              ) : (
                <Typography>
                  {latestRiskRatingResult?.calculatedRiskLevel ||
                    RISK_RATING_DATA_PLACEHOLDER}
                </Typography>
              )}
            </Item>
            <Item title="Occupation KYC details: ">
              {investorMarkerLoading ? (
                <CircularProgress size="1rem" />
              ) : (
                <Typography>{kycMarker ? "Not Held" : "Included"}</Typography>
              )}
            </Item>
            <Item title="Actual Risk Rating: ">
              <Stack direction="row" columnGap="24px">
                <MarkerDisplay
                  marker={riskRatingMarker}
                  loading={investorMarkerLoading}
                />
                <MarkerUpdateButton
                  markerCode={riskRatingMarker?.code}
                  disabled={amlDetailLoading || investorMarkerLoading}
                  onClick={() => {
                    if (riskRatingMarker) {
                      openMarkerUpdateDialog(riskRatingMarker, amlDetail!);
                    } else {
                      openGroupMarkerAddDialog(MarkerGroups.RSKS, amlDetail!);
                    }
                  }}
                />
              </Stack>
            </Item>
          </Stack>
        </Grid>
        <Grid item md={6} sm={12}>
          <Stack direction="column" rowGap="10px">
            <Item title="Calculated Risk Rating Date: ">
              {riskRatingLoading ? (
                <CircularProgress size="1rem" />
              ) : (
                <Typography>
                  {latestRiskRatingResult?.calculatedRiskRatingDate
                    ? formatTimestamp(
                      latestRiskRatingResult.calculatedRiskRatingDate
                    ).date
                    : RISK_RATING_DATA_PLACEHOLDER}
                </Typography>
              )}
            </Item>
            <Item title="Last Requested Date/Time: ">
              {requestsLoading ? (
                <CircularProgress size="1rem" />
              ) : (
                <Typography>
                  {lastRequestedAt || RISK_RATING_DATA_PLACEHOLDER}
                </Typography>
              )}
            </Item>
            <Item title="">
              <Button
                variant="contained"
                onClick={() => {
                  requestMutation.mutate();
                }}
                disabled={manualRiskCalcStatus !== 1 || requestMutation.isPending}
              >
                Calculate Risk Rating
              </Button>
            </Item>
            {riskEngineLink ? (
              <Link href={riskEngineLink} target="_blank">
                Risk Calculation in Enterprise Risk Engine
              </Link>
            ) : null}
          </Stack>
        </Grid>
      </Grid>
      <MarkerDialog
        open={markerDialogOpen}
        formConfig={markerDialogFormConfig!}
        initialValue={markerDialogInitial!}
        markers={markers}
        onClose={closeMarkerDialog}
        onSuccess={(data) => {
          closeMarkerDialog();
          refetchInvestorMarkers(personId, markers);
          removeHighlightedItem({
            id: data.previous!,
            type: ItemType.MARKER,
          });
        }}
      />
    </ProfileContainer>
  );
};

const Item: React.FC<{
  title: string;
  children?: ReactNode;
}> = ({ title, children }) => {
  return (
    <Grid alignItems="center" container sx={{ lineHeight: "32px" }}>
      <Grid item xs={4}>
        <Typography sx={{ fontStyle: "italic" }}>{title}</Typography>
      </Grid>
      <Grid item xs={6} sx={{ ml: "16.5%" }}>
        {children}
      </Grid>
    </Grid>
  );
};

export default RiskRatingProfile;
